import type { MarketingDiscoveredResource } from './marketingTypes';

/**
 * Helpers for choosing Google properties in the connect wizard (WP1.8). A
 * staff Google account can see every customer's properties, so the list is
 * filtered to "matches this customer" by default, searchable, and grouped by
 * the Google account (Analytics account, Ads manager, Business Profile
 * account) each property belongs to.
 */

export type DiscoveryFilter = 'matches' | 'all';

export type DiscoveryGroup = {
  key: string;
  label: string;
  items: MarketingDiscoveredResource[];
};

export function defaultDiscoveryFilter(items: MarketingDiscoveredResource[]): DiscoveryFilter {
  return items.some(item => item.matchesCustomer) ? 'matches' : 'all';
}

function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

export function filterDiscovery(
  items: MarketingDiscoveredResource[],
  { query = '', filter = 'all' }: { query?: string; filter?: DiscoveryFilter },
  keep: ReadonlySet<string> = new Set(),
): MarketingDiscoveredResource[] {
  const needle = fold(query.trim());
  return items.filter(item => {
    // Something already chosen never disappears behind a filter.
    if (keep.has(item.externalId)) return true;
    if (filter === 'matches' && !item.matchesCustomer) return false;
    if (!needle) return true;
    return fold(
      `${item.displayName} ${item.externalId} ${item.accountLabel || ''}`,
    ).includes(needle);
  });
}

export function groupDiscovery(items: MarketingDiscoveredResource[]): DiscoveryGroup[] {
  const groups = new Map<string, DiscoveryGroup>();
  for (const item of items) {
    const key = item.accountId || '';
    const group = groups.get(key) || {
      key,
      label: item.accountLabel || item.accountId || '',
      items: [],
    };
    group.items.push(item);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => {
    if (!a.label) return 1;
    if (!b.label) return -1;
    return a.label.localeCompare(b.label);
  });
}
